import React, { useState, useEffect, useMemo } from 'react';
import { rentalApi } from '../../../../api/rentalApi';
import { PDFDownloadLink } from '@react-pdf/renderer';
import RentalPDF from '../pdf/RentalPDF';

/**
 * Rental Contract List (Journal).
 * Displays a searchable and filterable list of all rental records.
 */
export default function RentalContractList() {
    const [rentals, setRentals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedRental, setSelectedRental] = useState(null);
    const [viewDetail, setViewDetail] = useState(null);
    const [globalContract, setGlobalContract] = useState({});

    // Filter State
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    useEffect(() => {
        fetchRentals();
        fetchContract();
    }, []);

    const fetchContract = async () => {
        try {
            const data = await rentalApi.getContract();
            setGlobalContract(data.contract_data || {});
        } catch (err) {
            console.error('Error fetching global contract', err);
        }
    };

    const fetchRentals = async () => {
        try {
            const data = await rentalApi.getRentals();
            setRentals(data);
        } catch (err) {
            console.error('Error fetching rentals:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchDetails = async (rentalId, mode = 'pdf') => {
        try {
            const details = await rentalApi.getRentalDetails(rentalId);
            if (mode === 'pdf') setSelectedRental(details);
            else setViewDetail(details);
        } catch (err) {
            alert('Daten konnten nicht geladen werden');
        }
    };

    const filteredRentals = useMemo(() => {
        return rentals.filter(r => {
            const matchesSearch = 
                (r.customer_name?.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (r.rental_number?.toLowerCase().includes(searchTerm.toLowerCase()));
            const matchesStatus = statusFilter === 'all' || r.status === statusFilter;
            const rDate = new Date(r.start_date);
            const matchesDate = (!dateFrom || rDate >= new Date(dateFrom)) && (!dateTo || rDate <= new Date(dateTo));
            return matchesSearch && matchesStatus && matchesDate;
        }).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }, [rentals, searchTerm, statusFilter, dateFrom, dateTo]);

    const getStatusLabel = (status) => {
        switch(status) {
            case 'draft': return 'Entwurf';
            case 'active': return 'Aktiv';
            case 'returned': return 'Beendet';
            case 'cancelled': return 'Storniert';
            default: return status;
        }
    };

    return (
        <div className="rental-contracts" style={{ animation: 'fadeInScale 0.3s ease' }}>
            <div className="compact-main-header" style={{ background: 'transparent', border: 'none', boxShadow: 'none', padding: '0 0 12px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '900', letterSpacing: '0.05em' }}>📒 VERMIETUNGS-JOURNAL</h3>
            </div>

            {/* Filter Bar */}
            <div className="premium-card" style={{ marginBottom: '20px', padding: '16px 20px', background: '#fff' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 2fr 1fr', gap: '16px', alignItems: 'end' }}>
                    <div className="form-group-compact">
                        <label style={{ fontSize: '0.6rem', fontWeight: '800', color: 'var(--p-slate-400)', marginBottom: '4px' }}>SUCHE</label>
                        <input type="text" placeholder="Kunde / Vorgang..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{ padding: '10px', fontSize: '0.8rem', borderRadius: '10px' }} />
                    </div>
                    <div className="form-group-compact">
                        <label style={{ fontSize: '0.6rem', fontWeight: '800', color: 'var(--p-slate-400)', marginBottom: '4px' }}>STATUS</label>
                        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ padding: '10px', fontSize: '0.8rem', borderRadius: '10px' }}>
                            <option value="all">Alle</option>
                            <option value="active">Aktiv</option>
                            <option value="returned">Beendet</option>
                        </select>
                    </div>
                    <div className="form-group-compact">
                        <label style={{ fontSize: '0.6rem', fontWeight: '800', color: 'var(--p-slate-400)', marginBottom: '4px' }}>ZEITRAUM (VON - BIS)</label>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ padding: '10px', fontSize: '0.75rem', borderRadius: '10px' }} />
                            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ padding: '10px', fontSize: '0.75rem', borderRadius: '10px' }} />
                        </div>
                    </div>
                    <button className="rental-btn-secondary" style={{ height: '40px', fontSize: '0.65rem' }} onClick={() => { setSearchTerm(''); setStatusFilter('all'); setDateFrom(''); setDateTo(''); }}>RESET</button>
                </div>
            </div>

            {loading ? <div className="rental-loading">JOURNAL LÄDT...</div> : (
                <div className="premium-card" style={{ padding: '0' }}>
                    <div className="modern-table-wrapper">
                        <table className="premium-style-table">
                            <thead>
                                <tr>
                                    <th style={{ paddingLeft: '20px' }}>NR.</th>
                                    <th>KUNDE</th>
                                    <th>DATUM</th>
                                    <th>STATUS</th>
                                    <th style={{ textAlign: 'right' }}>SUMME</th>
                                    <th style={{ textAlign: 'right', paddingRight: '20px' }}>AKTIONEN</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredRentals.map(r => (
                                    <tr key={r.id}>
                                        <td style={{ paddingLeft: '20px' }}>
                                            <button 
                                                onClick={() => fetchDetails(r.id, 'view')}
                                                style={{ border: 'none', background: 'none', padding: 0, fontWeight: '900', color: 'var(--optimox-blue)', fontSize: '0.85rem', cursor: 'pointer', textDecoration: 'underline' }}
                                            >
                                                {r.rental_number}
                                            </button>
                                        </td>
                                        <td>
                                            <div style={{ fontWeight: '800', fontSize: '0.85rem' }}>{r.customer_name}</div>
                                            <div style={{ fontSize: '0.6rem', color: 'var(--p-slate-400)' }}>{r.location || 'Ohne Projekt'}</div>
                                        </td>
                                        <td style={{ fontSize: '0.75rem', fontWeight: '600' }}>{new Date(r.start_date).toLocaleDateString('de-DE')}</td>
                                        <td>
                                            <span className={`status-pill ${r.status === 'active' ? 'active' : 'busy'}`} style={{ fontSize: '0.55rem' }}>
                                                {getStatusLabel(r.status).toUpperCase()}
                                            </span>
                                        </td>
                                        <td style={{ textAlign: 'right', fontWeight: '900', fontSize: '0.85rem' }}>{parseFloat(r.total_brutto || 0).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</td>
                                        <td style={{ textAlign: 'right', paddingRight: '20px' }}>
                                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                                {selectedRental?.id === r.id ? (
                                                    <PDFDownloadLink document={<RentalPDF rental={selectedRental} customer={{ company_name: selectedRental.customer_name, address: selectedRental.customer_address, email: selectedRental.customer_email, contact_person: selectedRental.customer_contact }} items={selectedRental.line_items || []} contractText={globalContract} />} fileName={`Miete_${r.rental_number}.pdf`} style={{ textDecoration: 'none' }}>
                                                        {({ loading }) => (
                                                            <button className="rental-btn-primary" style={{ padding: '4px 12px', fontSize: '0.65rem', background: '#10b981' }}>{loading ? '⌛' : '📂 DOWNLOAD'}</button>
                                                        )}
                                                    </PDFDownloadLink>
                                                ) : (
                                                    <button className="rental-btn-secondary" style={{ padding: '4px 10px', fontSize: '0.6rem' }} onClick={() => fetchDetails(r.id, 'pdf')}>📄 PDF</button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* VORGANGS-DETAIL MODAL */}
            {viewDetail && (
                <div className="action-modal-overlay" onClick={() => setViewDetail(null)}>
                    <div className="action-modal" style={{ width: '700px', maxWidth: '95vw', padding: '0', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
                        <div style={{ background: 'var(--p-slate-800)', padding: '24px', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <div style={{ fontSize: '0.65rem', fontWeight: '800', color: 'var(--p-slate-400)', textTransform: 'uppercase' }}>Vorgangsdetails</div>
                                <h2 style={{ margin: 0, fontSize: '1.2rem', fontWeight: '900' }}>#{viewDetail.rental_number}</h2>
                            </div>
                            <button onClick={() => setViewDetail(null)} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: 'white', width: '32px', height: '32px', borderRadius: '50%', cursor: 'pointer' }}>✕</button>
                        </div>

                        <div style={{ padding: '24px', maxHeight: '70vh', overflowY: 'auto' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px', marginBottom: '32px' }}>
                                <div>
                                    <h4 style={{ fontSize: '0.65rem', color: 'var(--p-slate-400)', marginBottom: '8px' }}>MIETER / KUNDE</h4>
                                    <div style={{ fontWeight: '800', fontSize: '1rem' }}>{viewDetail.customer_name}</div>
                                    <div style={{ fontSize: '0.8rem', color: 'var(--p-slate-600)', marginTop: '4px' }}>{viewDetail.customer_address}</div>
                                </div>
                                <div>
                                    <h4 style={{ fontSize: '0.65rem', color: 'var(--p-slate-400)', marginBottom: '8px' }}>EINSATZORT / ZEITRAUM</h4>
                                    <div style={{ fontWeight: '700', fontSize: '0.9rem' }}>{viewDetail.location || 'Kein Einsatzort hinterlegt'}</div>
                                    <div style={{ fontSize: '0.8rem', marginTop: '4px', color: 'var(--optimox-blue)', fontWeight: '700' }}>
                                        {new Date(viewDetail.start_date).toLocaleDateString('de-DE')} — {new Date(viewDetail.end_date).toLocaleDateString('de-DE')}
                                    </div>
                                </div>
                            </div>

                            <h4 style={{ fontSize: '0.65rem', color: 'var(--p-slate-400)', marginBottom: '12px' }}>POSITIONEN & KOSTEN</h4>
                            <div style={{ background: 'var(--p-slate-50)', borderRadius: '12px', overflow: 'hidden' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                                    <thead style={{ background: 'rgba(0,0,0,0.03)' }}>
                                        <tr>
                                            <th style={{ textAlign: 'left', padding: '12px' }}>BEZEICHNUNG</th>
                                            <th style={{ textAlign: 'center', padding: '12px' }}>MENGE</th>
                                            <th style={{ textAlign: 'right', padding: '12px' }}>GESAMT NETTO</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {viewDetail.line_items?.map((item, i) => (
                                            <tr key={i} style={{ borderBottom: '1px solid rgba(0,0,0,0.03)' }}>
                                                <td style={{ padding: '12px', fontWeight: '700' }}>{item.name} <small style={{ color: 'var(--p-slate-400)', display: 'block', fontSize: '0.65rem' }}>{parseFloat(item.unit_price_net).toFixed(2)} € / {item.pricing_type}</small></td>
                                                <td style={{ padding: '12px', textAlign: 'center' }}>{item.quantity}</td>
                                                <td style={{ padding: '12px', textAlign: 'right', fontWeight: '800' }}>{(item.quantity * item.unit_price_net).toFixed(2)} €</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end' }}>
                                <div style={{ width: '200px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', marginBottom: '4px' }}>
                                        <span>Summe Netto:</span>
                                        <span style={{ fontWeight: '700' }}>{parseFloat(viewDetail.total_net).toFixed(2)} €</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', marginBottom: '8px' }}>
                                        <span>MwSt. (19%):</span>
                                        <span style={{ fontWeight: '700' }}>{parseFloat(viewDetail.total_vat).toFixed(2)} €</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1.1rem', fontWeight: '900', color: 'var(--optimox-blue)', borderTop: '2px solid var(--optimox-blue)', paddingTop: '8px' }}>
                                        <span>GESAMT:</span>
                                        <span>{parseFloat(viewDetail.total_brutto).toFixed(2)} €</span>
                                    </div>
                                </div>
                            </div>
                            
                            <div style={{ marginTop: '32px' }}>
                                <h4 style={{ fontSize: '0.65rem', color: 'var(--p-slate-400)', marginBottom: '8px' }}>PROTOKOLL / ZUSTAND</h4>
                                <div style={{ padding: '16px', background: 'var(--p-blue-50)', borderRadius: '12px', fontSize: '0.85rem', color: 'var(--p-slate-700)', fontStyle: 'italic', border: '1px solid var(--p-blue-100)' }}>
                                    "{viewDetail.handover_condition || 'Keine Angaben zum Zustand vorhanden.'}"
                                </div>
                            </div>
                        </div>

                        <div style={{ background: 'var(--p-slate-50)', padding: '20px 24px', borderTop: '1px solid var(--p-slate-100)', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                            <button className="rental-btn-secondary" onClick={() => setViewDetail(null)}>Schließen</button>
                            <button className="rental-btn-primary" onClick={() => fetchDetails(viewDetail.id, 'pdf')}>Druckansicht öffnen</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
